'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { tmdb } from '@/lib/tmdb';

interface ContentItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  media_type?: 'movie' | 'tv';
}

export function AnimatedContentBackground() {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch multiple pages to get enough content for all rows
        const [
          trendingMoviesPage1,
          trendingMoviesPage2,
          trendingShowsPage1,
          trendingShowsPage2,
          popularMovies,
          popularShows
        ] = await Promise.all([
          tmdb.getTrendingMovies('week', 1),
          tmdb.getTrendingMovies('week', 2),
          tmdb.getTrendingShows('week', 1),
          tmdb.getTrendingShows('week', 2),
          tmdb.getPopularMovies(1),
          tmdb.getPopularShows(1)
        ]);

        // Combine all content and add media type
        const allContent: ContentItem[] = [
          ...trendingMoviesPage1.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...trendingMoviesPage2.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...trendingShowsPage1.results.map((item: any) => ({ ...item, media_type: 'tv' as const })),
          ...trendingShowsPage2.results.map((item: any) => ({ ...item, media_type: 'tv' as const })),
          ...popularMovies.results.map((item: any) => ({ ...item, media_type: 'movie' as const })),
          ...popularShows.results.map((item: any) => ({ ...item, media_type: 'tv' as const }))
        ];

        // Remove duplicates and filter items with posters
        const uniqueContent = allContent
          .filter((item, index, self) =>
            item.poster_path &&
            self.findIndex(i => i.id === item.id && i.media_type === item.media_type) === index
          )
          .slice(0, 120); // Take top 120 items for 6 rows of 20

        // Shuffle the array for random display
        const shuffledContent = uniqueContent.sort(() => Math.random() - 0.5);

        setContent(shuffledContent);
      } catch (error) {
        // Error fetching background content
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  if (loading || content.length === 0) {
    return (
      <div className="fixed inset-0 bg-black">
        {/* Fallback gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-red-500/20 to-red-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-red-600/15 to-red-800/5 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
      </div>
    );
  }

  // Create multiple rows for continuous scrolling effect
  const createRow = (startIndex: number, reverse: boolean = false) => {
    const rowContent = content.slice(startIndex, startIndex + 20);
    if (rowContent.length === 0) return null;

    return (
      <div
        key={startIndex}
        className={`flex gap-4 ${reverse ? 'animate-scroll-reverse' : 'animate-scroll'}`}
      >
        {/* Duplicate content for seamless loop */}
        {[...rowContent, ...rowContent].map((item, index) => {
          // Add subtle random rotation to each poster
          const rotation = (index % 7) - 3; // Values between -3 and 3 degrees
          const isDuplicate = index >= rowContent.length;

          return (
            <div
              key={`${item.id}-${item.media_type}-${startIndex}-${index}-${isDuplicate ? 'dup' : 'orig'}`}
              className="flex-shrink-0 relative group"
              style={{
                '--rotation': `${rotation}deg`,
                '--index': index,
                transform: `rotate(${rotation}deg)`
              } as React.CSSProperties}
            >
              <div className="w-32 h-48 lg:w-40 lg:h-60 relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <Image
                  src={tmdb.getImageUrl(item.poster_path, 'w300')}
                  alt={item.title || item.name || 'Content'}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110 group-hover:rotate-0"
                  sizes="(max-width: 1024px) 128px, 160px"
                  priority={startIndex === 0 && index < 6}
                />
                {/* Overlay for better text contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent group-hover:from-black/20 transition-all duration-300" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Dark overlay to ensure text readability and keep the backdrop neutral (no color bleed) */}
      <div className="absolute inset-0 bg-black/85 z-10" />

      {/* Animated content rows with rotation */}
      <div className="absolute -inset-20 flex flex-col justify-center gap-4 py-20 transform rotate-12 scale-125 overflow-hidden">
        {/* Row 1 - Moving right */}
        {createRow(0, false)}

        {/* Row 2 - Moving left */}
        {createRow(20, true)}

        {/* Row 3 - Moving right */}
        {createRow(40, false)}

        {/* Row 4 - Moving left */}
        {createRow(60, true)}

        {/* Row 5 - Moving right */}
        {createRow(80, false)}

        {/* Row 6 - Moving left (wraps to beginning if needed) */}
        {content.length > 20 && createRow(100, true)}
      </div>

      {/* Additional gradient overlay for better form visibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 z-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40 z-20" />

      <style jsx global>{`
        @keyframes scroll {
          0% {
            transform: translateX(0) translateY(0);
          }
          100% {
            transform: translateX(-50%) translateY(-2%);
          }
        }

        @keyframes scroll-reverse {
          0% {
            transform: translateX(-50%) translateY(-2%);
          }
          100% {
            transform: translateX(0) translateY(0);
          }
        }

        .animate-scroll {
          animation: scroll 60s linear infinite;
        }

        .animate-scroll-reverse {
          animation: scroll-reverse 60s linear infinite;
        }

        /* Pause animation on hover for better UX */
        .animate-scroll:hover,
        .animate-scroll-reverse:hover {
          animation-play-state: paused;
        }

        /* Add subtle floating effect to individual posters */
        .animate-scroll > div,
        .animate-scroll-reverse > div {
          animation: float 8s ease-in-out infinite;
          animation-delay: calc(var(--index, 0) * 0.2s);
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(var(--rotation, 0deg));
          }
          50% {
            transform: translateY(-10px) rotate(var(--rotation, 0deg));
          }
        }
      `}</style>
    </div>
  );
}